import { useState, useCallback } from 'react';
import {
  Filter,
  MapPin,
  Eye,
  Grid3X3,
  List,
  RefreshCw,
  AlertCircle,
  Image as ImageIcon,
  Loader2,
  Camera,
  Database,
  CheckSquare,
  Upload,
  Edit
} from 'lucide-react';
import { Button } from '@/components/ui';
import { PhotoDetailModal } from './components/PhotoDetailModal';
import { PhotoFilters } from './components/PhotoFilters';
import { ImpactPhotoCard } from './components/ImpactPhotoCard';
import { AssetEditorModal } from './components/AssetEditorModal';
import { DamPhotosTab } from './components/dam/DamPhotosTab';
import { UploadPhotosTab } from './components/UploadPhotosTab';
import { StatCard, StatGrid } from './components/StatCard';
import { useImpactPhotos } from './hooks/useImpactPhotos';
import { usePhotoUpload } from './hooks/usePhotoUpload';
import { ImpactPhoto, PhotoFilters as PhotoFiltersType } from './types/photo';

// Photo source types
type PhotoSource = 'impact' | 'dam' | 'upload';

export const PhotoSources = () => {
  const [activeSource, setActiveSource] = useState<PhotoSource>('impact');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState<ImpactPhoto | null>(null);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [showAssetEditor, setShowAssetEditor] = useState(false);
  const [filters, setFilters] = useState<PhotoFiltersType>({
    page_number: 1,
    page_size: 10,
    group_size: 20,
    status: 'Under Review'
  });

  // Custom hook for managing API calls (Impact Photos)
  const {
    photos: impactPhotos,
    villages: impactVillages,
    loading: impactLoading,
    loadingMore: impactLoadingMore,
    error: impactError,
    hasMoreVillages: impactHasMoreVillages,
    refetch: refetchImpact,
    updateFilters: updateImpactFilters,
    loadMoreVillages: loadMoreImpactVillages,
    loadMoreImagesForVillage: loadMoreImagesForImpactVillage
  } = useImpactPhotos(activeSource === 'impact' ? filters : { page_number: 1, page_size: 0 });

  // Photo upload hook
  const {
    uploadPhotos,
    progress: uploadProgress,
    error: uploadError,
    resetProgress,
    generateTitle,
    generateDescription,
    detectAssetType
  } = usePhotoUpload();

  // DAM selection mode (managed here so the header toggle works for both tabs)
  const [isDamSelectionMode, setIsDamSelectionMode] = useState(false);

  // Current active data based on selected source (Impact only - DAM has its own tab)
  const villages = impactVillages;
  const loading = activeSource === 'impact' ? impactLoading : false;
  const loadingMore = activeSource === 'impact' ? impactLoadingMore : false;
  const error = activeSource === 'impact' ? impactError : null;
  const hasMoreVillages = activeSource === 'impact' ? impactHasMoreVillages : false;
  const refetch = refetchImpact;
  const updateFilters = updateImpactFilters;
  const loadMoreVillages = loadMoreImpactVillages;
  const loadMoreImagesForVillage = loadMoreImagesForImpactVillage;

  // Update filters when they change
  const handleFiltersChange = useCallback((newFilters: Partial<PhotoFiltersType>) => {
    const updatedFilters = { ...filters, ...newFilters, page_number: 1 };
    setFilters(updatedFilters);
    updateFilters(updatedFilters);
  }, [filters, updateFilters]);


  // Handle photo selection for detailed view with village context
  const handlePhotoClick = useCallback((photo: ImpactPhoto, village?: any) => {
    // Enhance photo with village context for location details
    const enhancedPhoto = {
      ...photo,
      village_context: village ? {
        village_name: village.village_name,
        district_name: village.district_name,
        subdistrict_name: village.subdistrict_name,
        village_id: village.village_id
      } : null
    };
    setSelectedPhoto(enhancedPhoto as any);
  }, []);

  // Photo navigation in detail modal
  const handlePhotoNavigation = useCallback((direction: 'prev' | 'next') => {
    if (!selectedPhoto) return;

    const currentVillage = villages.find(v => 
      v.assets.some((asset: ImpactPhoto) => asset.image_id === selectedPhoto.image_id)
    );
    
    if (!currentVillage) return;

    const currentIndex = currentVillage.assets.findIndex(
      (asset: ImpactPhoto) => asset.image_id === selectedPhoto.image_id
    );
    
    let newIndex: number;
    if (direction === 'next') {
      newIndex = currentIndex < currentVillage.assets.length - 1 ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex > 0 ? currentIndex - 1 : currentVillage.assets.length - 1;
    }
    
    // Enhance the new photo with village context for location details
    const enhancedPhoto = {
      ...currentVillage.assets[newIndex],
      village_context: {
        village_name: currentVillage.village_name,
        district_name: currentVillage.district_name,
        subdistrict_name: currentVillage.subdistrict_name,
        village_id: currentVillage.village_id
      }
    };
    setSelectedPhoto(enhancedPhoto as any);
  }, [selectedPhoto, villages]);

  // Handle source change
  const handleSourceChange = useCallback((source: PhotoSource) => {
    setActiveSource(source);
    setSelectedPhotos(new Set());
    setIsSelectionMode(false);
    setIsDamSelectionMode(false);
    setFilters({
      page_number: 1,
      page_size: 10,
      group_size: 20,
      status: 'Under Review'
    });
  }, []);

  // Photo selection handlers
  const handlePhotoSelect = useCallback((photoId: string) => {
    setSelectedPhotos(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(photoId)) {
        newSelection.delete(photoId);
      } else {
        // Check if we've reached the maximum limit
        if (newSelection.size >= 5) {
          // Don't add more photos, could show a toast/alert here
          return newSelection;
        }
        newSelection.add(photoId);
      }
      return newSelection;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    const allPhotoIds = villages.flatMap(village => 
      village.assets
        .filter((photo: ImpactPhoto) => photo.status === 'Under Review')
        .map((photo: ImpactPhoto) => photo.image_id)
    );
    // Limit to first 5 photos
    const limitedPhotoIds = allPhotoIds.slice(0, 5);
    setSelectedPhotos(new Set(limitedPhotoIds));
  }, [villages]);

  const handleClearSelection = useCallback(() => {
    setSelectedPhotos(new Set());
  }, []);

  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(prev => !prev);
    if (isSelectionMode) {
      setSelectedPhotos(new Set());
    }
    resetProgress(); // Clear any previous upload progress
  }, [isSelectionMode, resetProgress]);

  // Show asset editor
  const handleShowAssetEditor = useCallback(() => {
    setShowAssetEditor(true);
  }, []);

  // Handle edited assets from modal
  const handleSaveEditedAssets = useCallback(async (editedAssets: any[]) => {
    setShowAssetEditor(false);
    
    const photoIds = Array.from(selectedPhotos);
    const result = await uploadPhotos(photoIds, editedAssets);
    
    if (result) {
      // If upload was successful, refresh the photos to get updated status
      if (result.success.length > 0) {
        refetchImpact();
        
        // Remove successfully uploaded photos from selection
        const successfulIds = new Set(result.success.map(s => s.id));
        setSelectedPhotos(prev => {
          const newSelection = new Set(prev);
          successfulIds.forEach(id => newSelection.delete(id));
          return newSelection;
        });
      }
    }
  }, [selectedPhotos, uploadPhotos, refetchImpact]);

  // Handle photo upload (quick upload without editing)
  const handleUploadPhotos = useCallback(async () => {
    if (selectedPhotos.size === 0) return;
    
    const photoIds = Array.from(selectedPhotos);
    
    // Create enhanced asset objects with metadata from selected photos
    const customAssets = photoIds.map(photoId => {
      // Find the photo in villages to get additional metadata
      let photoData: ImpactPhoto | null = null;
      for (const village of villages) {
        const foundPhoto = village.assets.find((asset: ImpactPhoto) => asset.image_id === photoId);
        if (foundPhoto) {
          photoData = foundPhoto;
          break;
        }
      }
      
      if (!photoData) {
        // Fallback if photo not found in current villages data
        return {
          id: photoId,
          title: generateTitle(photoId),
          description: generateDescription(photoId),
          source: 'Impact' as const,
          type: 'Image' as const
        };
      }
      
      // Find village context for proper title generation
      let villageContext = null;
      for (const village of villages) {
        if (village.assets.some((asset: ImpactPhoto) => asset.image_id === photoId)) {
          villageContext = {
            village_name: village.village_name,
            subdistrict_name: village.subdistrict_name,
            district_name: village.district_name
          };
          break;
        }
      }
      
      // Enhanced photo data with village context
      const enhancedPhotoData = {
        ...photoData,
        ...villageContext
      };
      
      // Create enhanced asset with generated metadata
      const title = photoData.title || generateTitle(photoId, enhancedPhotoData);
      const description = photoData.description || generateDescription(photoId, enhancedPhotoData);
      
      return {
        id: photoId,
        title,
        description,
        source: photoData.source || 'Impact' as const,
        type: photoData.type || 'Image' as const
      };
    });
    
    const result = await uploadPhotos(photoIds, customAssets);
    
    if (result) {
      // If upload was successful, refresh the photos to get updated status
      if (result.success.length > 0) {
        refetchImpact();
        
        // Remove successfully uploaded photos from selection
        const successfulIds = new Set(result.success.map(s => s.id));
        setSelectedPhotos(prev => {
          const newSelection = new Set(prev);
          successfulIds.forEach(id => newSelection.delete(id));
          return newSelection;
        });
      }
      
      // If all photos failed or some succeeded, keep selection mode active
      // User can decide when to exit selection mode
    }
  }, [selectedPhotos, uploadPhotos, refetchImpact, villages, generateTitle, generateDescription]);

  // Photo sources configuration
  const photoSources = [
    {
      key: 'impact' as PhotoSource,
      title: 'Impact Photos',
      description: 'Photos captured by Change Makers via Impact App',
      icon: Camera,
      color: 'blue',
      count: impactPhotos.length
    },
    {
      key: 'dam' as PhotoSource,
      title: 'DAM Photos',
      description: 'Professional photos from Brandfolder/DAM',
      icon: Database,
      color: 'green',
      count: 0
    },
    {
      key: 'upload' as PhotoSource,
      title: 'Upload Photos',
      description: 'Upload photos from your device to the asset library',
      icon: Upload,
      color: 'purple',
      count: 0
    }
  ];

  // Get total photo count
  const totalPhotos = villages.reduce((total, village) => total + village.assets.length, 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Photo Sources</h2>
              <p className="mt-1 text-sm text-gray-500">
                Browse and select photos from multiple sources for impact storytelling
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {/* Header controls only apply to the browse-style tabs.
                  Upload tab has its own primary CTA and metadata form. */}
              {activeSource !== 'upload' && (
                <>
                  {/* View Mode Toggle */}
                  <div className="flex items-center bg-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'grid'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <Grid3X3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-md transition-colors ${
                        viewMode === 'list'
                          ? 'bg-white text-gray-900 shadow-sm'
                          : 'text-gray-600 hover:text-gray-900'
                      }`}
                    >
                      <List className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Filters Toggle */}
                  <Button
                    variant="secondary"
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? 'bg-blue-50 border-blue-200' : ''}
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filters
                  </Button>

                  {/* Selection Toggle Button */}
                  {activeSource === 'impact' && (
                    <Button
                      variant={isSelectionMode ? 'primary' : 'secondary'}
                      onClick={toggleSelectionMode}
                    >
                      <CheckSquare className="h-4 w-4 mr-2" />
                      {isSelectionMode ? 'Exit Selection' : 'Select Photos'}
                    </Button>
                  )}
                  {activeSource === 'dam' && (
                    <Button
                      variant={isDamSelectionMode ? 'primary' : 'secondary'}
                      onClick={() => setIsDamSelectionMode((prev) => !prev)}
                    >
                      <CheckSquare className="h-4 w-4 mr-2" />
                      {isDamSelectionMode ? 'Exit Selection' : 'Select Photos'}
                    </Button>
                  )}

                  {/* Refresh Button */}
                  <Button variant="secondary" onClick={refetch} disabled={loading}>
                    <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Photo Source Tabs */}
          <div className="border-t pt-4">
            <nav className="flex space-x-4" aria-label="Photo Sources">
              {photoSources.map((source) => {
                const isActive = activeSource === source.key;
                const IconComponent = source.icon;

                return (
                  <button
                    key={source.key}
                    onClick={() => handleSourceChange(source.key)}
                    className={`
                      flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${isActive
                        ? `bg-${source.color}-100 text-${source.color}-700 border-2 border-${source.color}-200`
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100 border-2 border-transparent'
                      }
                    `}
                  >
                    <div className={`
                      flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center mr-3
                      ${isActive
                        ? `bg-${source.color}-500 text-white`
                        : `bg-${source.color}-100 text-${source.color}-600`
                      }
                    `}>
                      <IconComponent className="h-4 w-4" />
                    </div>
                    <div className="text-left">
                      <div className="font-medium">{source.title}</div>
                      <div className="text-xs opacity-75">{source.description}</div>
                    </div>
                    {source.count > 0 && (
                      <div className={`
                        ml-3 px-2 py-1 rounded-full text-xs font-medium
                        ${isActive
                          ? `bg-${source.color}-200 text-${source.color}-800`
                          : 'bg-gray-200 text-gray-600'
                        }
                      `}>
                        {source.count}
                      </div>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Per-tab stats now live inside each tab's content (Impact below,
            and inside DamPhotosTab / UploadPhotosTab) so the numbers are
            always relevant to the source the user is looking at. */}
      </div>

      {/* === DAM Photos Tab === */}
      {activeSource === 'dam' && (
        <DamPhotosTab
          isSelectionMode={isDamSelectionMode}
          onToggleSelectionMode={() => setIsDamSelectionMode(prev => !prev)}
        />
      )}

      {/* === Upload Photos Tab === */}
      {/* Note: `impact/upload` (the final step) is production-only per the
          API spec; staging works everywhere. In non-prod the user will see
          a clean per-card error explaining the env restriction. */}
      {activeSource === 'upload' && <UploadPhotosTab />}

      {/* === Impact Photos Tab === */}
      {activeSource === 'impact' && (
        <>
          {/* Stats specific to Impact data */}
          <StatGrid>
            <StatCard
              icon={ImageIcon}
              label="Total Photos"
              value={totalPhotos}
              variant="blue"
            />
            <StatCard
              icon={MapPin}
              label="Villages"
              value={villages.length}
              variant="green"
            />
            <StatCard
              icon={Eye}
              label="Status"
              value={filters.status || 'All'}
              variant="purple"
              compact
            />
            <StatCard
              icon={Camera}
              label="Active Source"
              value="Impact Photos"
              variant="orange"
              compact
            />
          </StatGrid>

          {/* Filters Panel  */}
          {showFilters && (
            <PhotoFilters
              filters={filters}
              onFiltersChange={handleFiltersChange}
              onClose={() => setShowFilters(false)}
            />
          )}

          {/* Bulk Actions Panel */}
          {isSelectionMode && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <span className="text-sm font-medium text-gray-900">
                    {selectedPhotos.size}/5 photo{selectedPhotos.size !== 1 ? 's' : ''} selected
                  </span>
                  {selectedPhotos.size >= 5 && (
                    <span className="text-xs text-amber-600 font-medium">
                      Maximum limit reached
                    </span>
                  )}
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleSelectAll}
                      disabled={selectedPhotos.size >= 5}
                    >
                      Select First 5
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handleClearSelection}
                      disabled={selectedPhotos.size === 0}
                    >
                      Clear Selection
                    </Button>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="secondary"
                    onClick={handleShowAssetEditor}
                    disabled={selectedPhotos.size === 0 || uploadProgress.isUploading}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Metadata{selectedPhotos.size > 0 ? ` (${selectedPhotos.size})` : ''}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleUploadPhotos}
                    disabled={selectedPhotos.size === 0 || uploadProgress.isUploading}
                  >
                    {uploadProgress.isUploading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Uploading... ({uploadProgress.completed}/{uploadProgress.total})
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Quick Upload ({selectedPhotos.size})
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Upload Status Messages */}
          {uploadError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Upload Failed</h3>
                  <p className="mt-2 text-sm text-red-700">{uploadError}</p>
                  <div className="mt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={resetProgress}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!uploadProgress.isUploading && uploadProgress.total > 0 && !uploadError && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckSquare className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Upload Complete</h3>
                  <div className="mt-2 text-sm text-green-700">
                    <p>
                      Successfully uploaded {uploadProgress.completed} of {uploadProgress.total} photos to Asset Library.
                      {uploadProgress.failed > 0 && (
                        <span className="block mt-1">
                          {uploadProgress.failed} photo{uploadProgress.failed !== 1 ? 's' : ''} failed to upload.
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="mt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={resetProgress}
                    >
                      Dismiss
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Error loading photos</h3>
                  <p className="mt-2 text-sm text-red-700">{error}</p>
                  <div className="mt-4">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={refetch}
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">Loading photos...</p>
              </div>
            </div>
          )}

          {/* Photos Grid/List */}
          {!loading && !error && villages.length > 0 && (
            <div className="space-y-8">
              {villages.map((village) => (
                <div key={village.village_id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Village Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{village.village_name}</h3>
                        <p className="text-sm text-gray-600">
                          {village.subdistrict_name}, {village.district_name}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {village.assets.length} Photos
                        </p>
                        {village.has_next_page && (
                          <p className="text-xs text-blue-600">More available</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Photos */}
                  <div className="p-6">
                    {viewMode === 'grid' ? (
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                        {village.assets.map((photo: ImpactPhoto) => (
                          <ImpactPhotoCard
                            key={photo.image_id}
                            photo={photo}
                            viewMode="grid"
                            onClick={() => handlePhotoClick(photo, village)}
                            selected={selectedPhotos.has(photo.image_id)}
                            isSelectionMode={isSelectionMode}
                            onSelect={handlePhotoSelect}
                            selectionDisabled={selectedPhotos.size >= 5 && !selectedPhotos.has(photo.image_id)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {village.assets.map((photo: ImpactPhoto) => (
                          <ImpactPhotoCard
                            key={photo.image_id}
                            photo={photo}
                            viewMode="list"
                            onClick={() => handlePhotoClick(photo, village)}
                            selected={selectedPhotos.has(photo.image_id)}
                            isSelectionMode={isSelectionMode}
                            onSelect={handlePhotoSelect}
                            selectionDisabled={selectedPhotos.size >= 5 && !selectedPhotos.has(photo.image_id)}
                          />
                        ))}
                      </div>
                    )}

                    {/* Load More Images for this Village */}
                    {village.has_next_page && (
                      <div className="mt-6 text-center">
                        <Button
                          variant="secondary"
                          onClick={() => loadMoreImagesForVillage(village.village_id)}
                          disabled={loadingMore}
                          className="px-6"
                        >
                          {loadingMore ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Loading More Photos...
                            </>
                          ) : (
                            <>
                              Load More Photos ({village.assets.length} shown)
                            </>
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {/* Load More Villages Button */}
              {hasMoreVillages && (
                <div className="mt-8 text-center">
                  <Button
                    variant="primary"
                    onClick={loadMoreVillages}
                    disabled={loadingMore}
                    size="lg"
                    className="px-8"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        Loading More Villages...
                      </>
                    ) : (
                      <>
                        Load More Villages ({villages.length} shown)
                      </>
                    )}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Empty State */}
          {!loading && !error && villages.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
              <div className="text-center">
                <Camera className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Impact Photos Found</h3>
                <p className="text-gray-500 mb-6">
                  No photos from the Impact App match your current filters. Try adjusting your filters or check back later for new photos.
                </p>
                <Button
                  variant="secondary"
                  onClick={() => {
                    const resetFilters = {
                      page_number: 1,
                      page_size: 10,
                      group_size: 20,
                      status: 'Under Review' as const,
                      district_name: undefined,
                      subdistrict_name: undefined,
                      village_id: undefined,
                      village_name: undefined,
                      program: undefined,
                      activity: undefined,
                    };
                    setFilters(resetFilters);
                    updateFilters(resetFilters);
                  }}
                >
                  Reset Filters
                </Button>
              </div>
            </div>
          )}
        </>
      )}


      {/* Photo Detail Modal */}
      {selectedPhoto && (
        <PhotoDetailModal
          photo={selectedPhoto}
          onClose={() => setSelectedPhoto(null)}
          onPrevious={() => handlePhotoNavigation('prev')}
          onNext={() => handlePhotoNavigation('next')}
        />
      )}

      {/* Asset Editor Modal */}
      {showAssetEditor && (
        <AssetEditorModal
          assets={Array.from(selectedPhotos).map(photoId => {
            // Find the photo and village context
            let photoData: ImpactPhoto | undefined;
            let villageContext = null;
            
            for (const village of villages) {
              const foundPhoto = village.assets.find((asset: ImpactPhoto) => asset.image_id === photoId);
              if (foundPhoto) {
                photoData = foundPhoto;
                villageContext = {
                  village_name: village.village_name,
                  subdistrict_name: village.subdistrict_name,
                  district_name: village.district_name,
                  village_id: village.village_id
                };
                break;
              }
            }
            
            // Enhanced photo data with village context
            const enhancedPhotoData = photoData ? {
              ...photoData,
              ...villageContext
            } : undefined;
            
            return { photoId, photoData: enhancedPhotoData };
          })}
          onSave={handleSaveEditedAssets}
          onClose={() => setShowAssetEditor(false)}
          generateTitle={generateTitle}
          generateDescription={generateDescription}
          detectAssetType={detectAssetType}
        />
      )}
    </div>
  );
};